import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik';
import i18n from 'i18n';
import { get } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LineMessageType } from 'core/advertiser/LineBeacon';
import { OutdoorLineMessageField } from 'containers/Creatives/CreativeSetupFlow/FlowSteps/SubSteps/FormContent/hooks/OutdoorLineMessageField';
import { WithAddIcon } from 'components/common/WithAddIcon/WithAddIcon';
import styles from 'containers/Creatives/CreativeSetupFlow/FlowSteps/SubSteps/FormContent/hooks/outdoorFields.module.scss';
import { FormikField } from 'components/common/form/field/FormikField';
import { useCallAPI } from 'hooks/useCallAPI';
import { DefaultAdvertiserManager } from 'core/advertiser/AdvertiserManager';
import { LoadingIndicator } from 'components/common/LoadingIndicator';

const advertiserManager = new DefaultAdvertiserManager();

export const renderOutdoorLineMessageFields = (
  advertiserId: number | null | undefined,
  fieldPath = ''
) => {
  return (
    <OutdoorLineMesssageFields
      advertiserId={advertiserId}
      fieldPath={fieldPath}
    />
  );
};

export const OutdoorLineMesssageFields: React.FC<{
  advertiserId: number | null | undefined;
  fieldPath?: string;
}> = ({ advertiserId, fieldPath = '' }) => {
  const { loading, callAPIs } = useCallAPI();

  const { values } = useFormikContext<any>();

  const selectedBeaconConnection = get(
    values,
    `${fieldPath}typeProperties.beaconConnection`
  );

  const [couponOptions, setCouponOptions] = useState<SelectOptions[]>([]);
  const couponCacheRef = useRef<Record<string, SelectOptions[]>>({});

  useEffect(() => {
    if (!selectedBeaconConnection || !advertiserId) {
      setCouponOptions([]);
      return;
    }
    const cacheKey = `${advertiserId}-${selectedBeaconConnection.value}`;
    if (couponCacheRef.current[cacheKey]) {
      setCouponOptions(couponCacheRef.current[cacheKey]);
      return;
    }
    callAPIs(
      [
        () =>
          advertiserManager.getLineBeaconCoupons(
            advertiserId,
            selectedBeaconConnection.value.toString()
          )
      ],
      (options: SelectOptions[]) => {
        couponCacheRef.current[cacheKey] = options;
        setCouponOptions(options);
      }
    );
  }, [advertiserId, selectedBeaconConnection, callAPIs]);

  const renderOutdoorLineMessageFields = useCallback(
    ({ name, form, push, remove, replace }: FieldArrayRenderProps) => {
      const lineMessages = get(form.values, name);
      const onNewButtonClicked = () => {
        push({
          type: LineMessageType.TEXT
        });
      };

      const newButton = (
        <FormikField.Custom label=''>
          <WithAddIcon
            className={styles.filterAdder}
            onClick={onNewButtonClicked}
          >
            {i18n.t('outdoorLineMessageFields.labels.addLineMessage')}
          </WithAddIcon>
        </FormikField.Custom>
      );

      if (!lineMessages) {
        return null;
      }

      const fields = lineMessages.map((lineMessage, index) => {
        const removeField = () => remove(index);
        const fieldName = `${name}.${index}`;
        const onTypeChange = (type: LineMessageType) => {
          replace(index, {
            type
          });
        };
        return (
          <OutdoorLineMessageField
            key={fieldName}
            fieldName={fieldName}
            lineMessage={lineMessage}
            onTypeChange={onTypeChange}
            removeField={removeField}
            couponOptions={couponOptions}
          />
        );
      });
      return (
        <>
          <div>{fields}</div>
          {lineMessages.length < 5 && newButton}
        </>
      );
    },
    [couponOptions]
  );

  const lineMessageFieldName = `${fieldPath}lineMessage`;

  return (
    <>
      {loading && <LoadingIndicator />}
      <FieldArray
        name={lineMessageFieldName}
        render={renderOutdoorLineMessageFields}
      />
    </>
  );
};
